import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Instagram, Linkedin, Facebook, Youtube, ArrowRight } from "lucide-react";
import { COMPANY } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
    "Residential — 3D Exterior",
    "Interior Design",
    "Walkthrough / Animation",
    "VR Experience",
    "Commercial Building",
    "Church / Institutional",
    "Renovation Visualization",
    "Other",
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        project_type: "",
        budget: "",
        location: "",
        message: "",
    });
    const [busy, setBusy] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
            toast.error("Please fill in your name, email and phone.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/leads`, { ...form, source: "contact_section" });
            toast.success("Thanks! We'll be in touch within 24 hours.");
            setForm({
                name: "",
                email: "",
                phone: "",
                project_type: "",
                budget: "",
                location: "",
                message: "",
            });
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section id="contact" data-testid="contact" className="relative py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Info */}
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="divider-gold" />
                            <span className="uppercase tracking-[0.28em] text-xs text-gold font-medium">
                                Contact
                            </span>
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight">
                            Let&apos;s design your future.
                        </h2>
                        <p className="mt-5 text-foreground/70 max-w-md">
                            Share a few details and we&apos;ll respond with a plan, timeline, and quote.
                            Free consultations for every serious inquiry.
                        </p>

                        <div className="mt-10 space-y-5">
                            <a
                                href={`tel:${COMPANY.phoneIntl}`}
                                data-testid="contact-phone"
                                className="flex items-center gap-4 group"
                            >
                                <span className="h-11 w-11 rounded-full border hairline grid place-items-center group-hover:border-gold transition">
                                    <Phone className="h-4 w-4" />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Phone</div>
                                    <div className="font-display text-lg">{COMPANY.phone}</div>
                                </div>
                            </a>
                            <a
                                href={`mailto:${COMPANY.email}`}
                                data-testid="contact-email"
                                className="flex items-center gap-4 group"
                            >
                                <span className="h-11 w-11 rounded-full border hairline grid place-items-center group-hover:border-gold transition">
                                    <Mail className="h-4 w-4" />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Email</div>
                                    <div className="font-display text-lg break-all">{COMPANY.email}</div>
                                </div>
                            </a>
                            <div className="flex items-center gap-4">
                                <span className="h-11 w-11 rounded-full border hairline grid place-items-center">
                                    <MapPin className="h-4 w-4" />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Studio</div>
                                    <div className="font-display text-lg">{COMPANY.location}</div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex items-center gap-3">
                            <SocialLink href={COMPANY.socials.instagram} label="Instagram" testid="social-instagram">
                                <Instagram className="h-4 w-4" />
                            </SocialLink>
                            <SocialLink href={COMPANY.socials.linkedin} label="LinkedIn" testid="social-linkedin">
                                <Linkedin className="h-4 w-4" />
                            </SocialLink>
                            <SocialLink href={COMPANY.socials.facebook} label="Facebook" testid="social-facebook">
                                <Facebook className="h-4 w-4" />
                            </SocialLink>
                            <SocialLink href={COMPANY.socials.youtube} label="YouTube" testid="social-youtube">
                                <Youtube className="h-4 w-4" />
                            </SocialLink>
                        </div>

                        <div className="mt-10 rounded-2xl overflow-hidden border hairline">
                            <iframe
                                title="Studio location"
                                width="100%"
                                height="240"
                                loading="lazy"
                                className="block"
                                referrerPolicy="no-referrer-when-downgrade"
                                src="https://www.google.com/maps?q=Hyderabad%2C+Telangana%2C+India&output=embed"
                            />
                        </div>
                    </div>

                    {/* Form */}
                    <motion.form
                        onSubmit={submit}
                        data-testid="contact-form"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-7 p-6 md:p-10 rounded-2xl border hairline bg-card"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <Field label="Your Name" testid="input-name" value={form.name} onChange={update("name")} required />
                            <Field label="Email" type="email" testid="input-email" value={form.email} onChange={update("email")} required />
                            <Field label="Phone" testid="input-phone" value={form.phone} onChange={update("phone")} required />
                            <Select
                                label="Project Type"
                                testid="input-project-type"
                                value={form.project_type}
                                onChange={update("project_type")}
                                options={PROJECT_TYPES}
                            />
                            <Field label="Budget (optional)" testid="input-budget" value={form.budget} onChange={update("budget")} placeholder="₹5 – 15 Lakhs" />
                            <Field label="Location" testid="input-location" value={form.location} onChange={update("location")} placeholder="City, State" />
                        </div>
                        <div className="mt-5">
                            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">
                                Tell us about your project
                            </label>
                            <textarea
                                value={form.message}
                                onChange={update("message")}
                                data-testid="input-message"
                                rows={5}
                                placeholder="A brief description of what you're planning…"
                                className="w-full rounded-lg bg-background border hairline px-4 py-3 text-sm focus:outline-none focus:border-gold transition"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="submit-contact"
                            className="mt-6 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-gold text-white font-medium hover:brightness-110 transition disabled:opacity-50"
                        >
                            {busy ? "Sending…" : "Send Enquiry"}
                            <ArrowRight className="h-4 w-4" />
                        </button>
                        <p className="mt-3 text-xs text-muted-foreground">
                            We respond within 24 hours. Your details stay private.
                        </p>
                    </motion.form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, testid, value, onChange, type = "text", required, placeholder }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">
                {label} {required && <span className="text-gold">*</span>}
            </span>
            <input
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full h-11 rounded-lg bg-background border hairline px-4 text-sm focus:outline-none focus:border-gold transition"
            />
        </label>
    );
}

function Select({ label, testid, value, onChange, options }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">
                {label}
            </span>
            <select
                value={value}
                onChange={onChange}
                data-testid={testid}
                className="w-full h-11 rounded-lg bg-background border hairline px-3 text-sm focus:outline-none focus:border-gold transition"
            >
                <option value="">Select…</option>
                {options.map((o) => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </select>
        </label>
    );
}

function SocialLink({ href, label, testid, children }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={label}
            data-testid={testid}
            className="h-10 w-10 rounded-full border hairline grid place-items-center hover:border-gold hover:text-gold transition"
        >
            {children}
        </a>
    );
}
